const STORAGE_KEY = 'moss-side-scoring';
const ARCHIVE_PREFIX = 'moss-side-scoring:archive:';
const ARCHIVE_INDEX_KEY = 'moss-side-scoring:archives';

export const saveAppState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save app state', error);
  }
};

export const loadAppState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to load app state', error);
    return null;
  }
};

export const clearAppState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear app state', error);
  }
};

const loadArchiveIndex = () => {
  try {
    const raw = localStorage.getItem(ARCHIVE_INDEX_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load archive index', error);
    return [];
  }
};

const saveArchiveIndex = (entries) => {
  try {
    localStorage.setItem(ARCHIVE_INDEX_KEY, JSON.stringify(entries));
  } catch (error) {
    console.error('Failed to save archive index', error);
  }
};

const buildArchiveEntry = (state, key, savedAt, label) => {
  const match = state && state.store ? state.store.match || {} : {};
  const metadata = match.metadata || {};
  const opponent = metadata.opposition || '';
  const competition = metadata.competition || '';
  const date = metadata.date || '';
  const homeAway = metadata.homeAway || '';
  const titleParts = [];
  if (homeAway) {
    titleParts.push(homeAway);
  }
  titleParts.push(`vs ${opponent || 'Unknown'}`);
  const title = titleParts.join(' ');
  return {
    key,
    savedAt,
    label,
    title,
    date,
    opponent,
    competition,
    homeAway,
    winner: match.winner || null,
    finished: Boolean(match.finished),
    sets: Array.isArray(match.sets) ? match.sets.length : 0
  };
};

const loadArchivePayload = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to load archive payload', error);
    return null;
  }
};

const rebuildArchiveIndex = () => {
  const entries = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(ARCHIVE_PREFIX)) {
      continue;
    }
    const payload = loadArchivePayload(key);
    if (!payload || !payload.state) {
      continue;
    }
    entries.push(buildArchiveEntry(payload.state, key, payload.savedAt || '', payload.label || ''));
  }
  saveArchiveIndex(entries);
  return entries;
};

export const archiveAppState = (state, label = '') => {
  try {
    const savedAt = new Date().toISOString();
    const suffix = label ? `${savedAt}-${label}` : savedAt;
    const key = `${ARCHIVE_PREFIX}${suffix}`;
    const payload = { savedAt, label, state };
    localStorage.setItem(key, JSON.stringify(payload));
    const entry = buildArchiveEntry(state, key, savedAt, label);
    const index = loadArchiveIndex();
    const nextIndex = [entry, ...index.filter(item => item.key !== key)];
    saveArchiveIndex(nextIndex);
    return key;
  } catch (error) {
    console.error('Failed to archive app state', error);
    return null;
  }
};

export const getArchiveIndex = () => {
  const index = loadArchiveIndex();
  const entries = index.length ? index : rebuildArchiveIndex();
  return entries
    .slice()
    .sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || '')));
};

export { loadArchivePayload };

export const loadArchiveState = (key) => {
  const payload = loadArchivePayload(key);
  return payload ? payload.state : null;
};

export const deleteArchive = (key) => {
  try {
    localStorage.removeItem(key);
    const nextIndex = loadArchiveIndex().filter(item => item.key !== key);
    saveArchiveIndex(nextIndex);
    return true;
  } catch (error) {
    console.error('Failed to delete archive', error);
    return false;
  }
};

export const backfillArchivedMatches = (compactState) => {
  if (typeof compactState !== 'function') {
    return 0;
  }
  let updated = 0;
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(ARCHIVE_PREFIX)) {
      continue;
    }
    const payload = loadArchivePayload(key);
    if (!payload || !payload.state) {
      continue;
    }
    const nextState = compactState(payload.state);
    if (!nextState) {
      continue;
    }
    const nextPayload = { ...payload, state: nextState };
    try {
      localStorage.setItem(key, JSON.stringify(nextPayload));
      updated += 1;
    } catch (error) {
      console.error('Failed to backfill archive', error);
    }
  }
  if (updated > 0) {
    rebuildArchiveIndex();
  }
  return updated;
};
